import React from "react";
import { Box, Typography } from "@mui/material";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import { AnimatePresence, motion } from "framer-motion";
import { NavIcon } from "./navIcons";
import { NavGroup as NavGroupModel, NavPage } from "./navModel";
import { IconTile, isPlainClick } from "./navPrimitives";
import { NAV, rowSx } from "./navTheme";

interface NavGroupProps {
  group: NavGroupModel;
  isOpen: boolean;
  activeLink: string;
  touch?: boolean;
  onToggle: (groupId: string) => void;
  onNavigate: (page: NavPage) => void;
}

const NavGroupSection: React.FC<NavGroupProps> = ({
  group,
  isOpen,
  activeLink,
  touch = false,
  onToggle,
  onNavigate,
}) => {
  const rowHeight = touch ? NAV.rowHeightTouch : NAV.rowHeightDesktop;
  const containsActive = group.pages.some((page) => page.link === activeLink);

  return (
    <Box sx={{ mb: 0.25 }}>
      <Box
        component={motion.button}
        type="button"
        whileTap={{ scale: 0.985 }}
        transition={NAV.spring}
        onClick={() => onToggle(group.id)}
        aria-expanded={isOpen}
        sx={{
          ...rowSx({ height: rowHeight, radius: 12 }),
          width: "100%",
          color: containsActive || isOpen ? NAV.textActive : NAV.textIdle,
        }}
      >
        <IconTile active={containsActive}>
          <NavIcon id={group.id} variant="group" size={18} color="inherit" />
        </IconTile>

        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: touch ? "0.9375rem" : "0.875rem",
            fontWeight: containsActive ? 650 : 550,
            color: "inherit",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {group.label}
        </Typography>

        {/* Collapsed group holding the current page still shows where you are. */}
        {containsActive && !isOpen && (
          <Box
            component={motion.span}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={NAV.spring}
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: NAV.accent,
              flexShrink: 0,
            }}
          />
        )}

        <Box
          component={motion.span}
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={NAV.spring}
          sx={{ display: "flex", color: NAV.textMuted, flexShrink: 0 }}
        >
          <KeyboardArrowRightRounded sx={{ fontSize: 20 }} />
        </Box>
      </Box>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="pages"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={NAV.springSoft}
            style={{ overflow: "hidden" }}
          >
            <Box
              sx={{
                pl: 2.5,
                pt: 0.25,
                pb: 0.5,
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 21,
                  top: 4,
                  bottom: 8,
                  width: "1px",
                  backgroundColor: NAV.border,
                },
              }}
            >
              {group.pages.map((page) => {
                const isActive = page.link === activeLink;
                return (
                  <Box
                    key={page.id}
                    component="a"
                    href={page.link}
                    onClick={(event: React.MouseEvent) => {
                      if (!isPlainClick(event)) return;
                      event.preventDefault();
                      onNavigate(page);
                    }}
                    aria-current={isActive ? "page" : undefined}
                    sx={{
                      ...rowSx({
                        height: rowHeight - (touch ? 2 : 4),
                        radius: 10,
                      }),
                      position: "relative",
                      ml: 0.5,
                      pr: 1,
                      color: isActive ? NAV.textActive : NAV.textIdle,
                      "@media (hover: hover)": {
                        "&:hover": {
                          color: NAV.textActive,
                          backgroundColor: isActive ? "transparent" : NAV.hover,
                        },
                      },
                    }}
                  >
                    {isActive && (
                      <Box
                        component={motion.span}
                        layoutId="nav-active-pill"
                        transition={NAV.spring}
                        sx={{
                          position: "absolute",
                          inset: 0,
                          borderRadius: "10px",
                          backgroundColor: NAV.accentSoft,
                          border: `1px solid ${NAV.accentBorder}`,
                          pointerEvents: "none",
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        flexShrink: 0,
                        zIndex: 1,
                        color: isActive ? NAV.accent : "inherit",
                      }}
                    >
                      <NavIcon
                        id={page.id}
                        groupId={group.id}
                        size={17}
                        color="inherit"
                      />
                    </Box>
                    <Typography
                      sx={{
                        zIndex: 1,
                        minWidth: 0,
                        fontSize: touch ? "0.875rem" : "0.8125rem",
                        fontWeight: isActive ? 600 : 450,
                        color: "inherit",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {page.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default NavGroupSection;
